/**
 * Auriga Frontend Application
 *
 * This component serves as the root of the Auriga Frontend application.
 * It sets up the application structure, providers, and routing.
 */

import { GFTMuiTheme } from '@amzn/gft-frontend-commons/styles';
import { GFTServiceClientProvider } from '@amzn/gft-service-client';
import ApiGuard from '@components/Api/ApiGuard';
import AuthGuard from '@components/Auth/AuthGuard';
// Core Auriga layout component for rendering as child
import AurigaLayout from '@components/Layout/AurigaLayout';
import { AurigaContextProvider } from '@context/AurigaContext';
import { UserProvider } from '@context/UserContext';
import { createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import ApiProvider from '@services/api/ApiProvider';
import dimSumApiConfig from '@services/api/dimSum/dimSum.config';
import { AuthProvider } from '@utils/auth/AuthProvider';
import { getCognitoSettings } from '@utils/auth/settings';
import { AurigaRumConfig, generateRumConfig, RUM_ENABLED_ENVIRONMENTS } from '@utils/rum/cloudWatchRUMHelpers';
import RumMetricsLogger from '@utils/rum/RumMetricsLogger';
import { includes } from 'lodash';
import { useEffect, useState } from 'react';

/**
 * Auriga theme — extends GFTMuiTheme with app-specific custom palette colors.
 * CSS variables enabled with light/dark color schemes for dark mode support.
 * Toggle with MUI's `useColorScheme()` hook anywhere in the tree.
 *
 * Uses two-argument createTheme: first arg resolves into a full theme,
 * second arg deep-merges custom palette colors with augmentColor for explicit
 * light/dark/contrastText generation.
 *
 * See: https://mui.com/material-ui/customization/palette/#custom-colors
 */
const aurigaTheme = createTheme({
  ...GFTMuiTheme,
  cssVariables: {
    colorSchemeSelector: 'data',
  },
  colorSchemes: { light: true, dark: true },
  palette: {
    ...GFTMuiTheme.palette,
    white: GFTMuiTheme.palette.augmentColor({
      color: { main: '#FFFFFF' },
      name: 'white',
    }),
    // 'customGrey' avoids conflict with MUI's built-in palette.grey (Color scale type)
    customGrey: GFTMuiTheme.palette.augmentColor({
      color: { main: '#888888' },
      name: 'customGrey',
    }),
  },
});

/**
 * App Component
 *
 * This component serves as the root of the application and:
 * - Sets up authentication with AuthProvider
 * - Protects routes with AuthGuard
 * - Configures API access with ApiProvider
 * - Initializes RUM monitoring with RumMetricsLogger class
 * - Establishes the application layout with AurigaLayout
 * - Sets up routing with React Router
 * - Applies the Auriga theme with Amazon Ember font and CSS variable dark mode support
 *
 * The component structure follows a provider pattern where each provider
 * adds functionality to the application and makes it available to child components.
 */
const App = () => {
  // For single instance of Metrics Logger for use in application
  const [rumMetricsLogger, setRumMetricsLogger] = useState<RumMetricsLogger | null>(null);

  useEffect(() => {
    // Initialize CloudWatchRum App monitor on any Auriga Application load
    const rumConfig: AurigaRumConfig | null = generateRumConfig();
    if (rumConfig) {
      setRumMetricsLogger(new RumMetricsLogger(rumConfig));
    } else if (includes(RUM_ENABLED_ENVIRONMENTS, process.env.NODE_ENV)) {
      console.error('RUM not initialized');
    } else {
      console.debug('RUM not allowed for current environment');
    }
  }, []);

  // Get the Cognito settings for grabbing the Identity Provider
  const settings = getCognitoSettings();

  return (
    <ThemeProvider theme={aurigaTheme} defaultMode='light'>
      <CssBaseline />
      <AuthProvider>
        <AuthGuard>
          <UserProvider>
            <ApiProvider>
              <ApiGuard>
                <GFTServiceClientProvider
                  serviceConfigs={[dimSumApiConfig]}
                  cognitoConfig={{
                    region: settings.region,
                    userPoolId: settings.userPoolId,
                    userPoolClientId: settings.userPoolWebClientId,
                    identityPoolId: settings.identityPoolId,
                  }}
                >
                  <AurigaContextProvider rumLogger={rumMetricsLogger}>
                    <AurigaLayout />
                  </AurigaContextProvider>
                </GFTServiceClientProvider>
              </ApiGuard>
            </ApiProvider>
          </UserProvider>
        </AuthGuard>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
